import api from "../../../utils/api";
import { unwrap, unwrapList } from "./apiEnvelope";

// ===========================================================================
// Facturas de negocios — servicio (crm_invoices).
// ===========================================================================
// Una factura sale de cuatro lugares: de la fecha de corte del negocio («corte»),
// de un ingreso que entró («ingreso»), de alguien que la capturó («manual») o de
// una cotización que el cliente aceptó («cotizacion», #1546).
// Todo ingreso de un cliente tiene la suya. El backend manda la factura al
// cliente en la fecha de corte y le confirma el pago cuando entra el ingreso o
// cuando aquí se marca pagada.
// ===========================================================================

export type InvoiceStatus = "pendiente" | "enviada" | "parcial" | "pagada" | "cancelada";
export type InvoiceSource = "corte" | "ingreso" | "manual" | "cotizacion";

/** Renglón de la cotización de la que nació la factura (#1546). */
export interface InvoiceLine {
  name: string;
  sku: string | null;
  quantity: number;
  unitPrice: number | null;
  discountPct: number;
  taxRate: number | null;
  amount: number;
}

export interface InvoicePayment {
  incomeId: string | null;
  amount: number;
  date: string;
  payer: string | null;
  /** Registrado con «Marcar pagada», no por un ingreso. */
  manual: boolean;
}

export interface InvoiceDelivery {
  type: "factura" | "pago";
  at: string;
  to: string[];
  ok: boolean;
  error: string | null;
}

export interface Invoice {
  id: string;
  number: string;
  dealId: string | null;
  dealTitle: string | null;
  client: string;
  /** Razón social que paga. */
  payer: string | null;
  concept: string;
  /** "2026-10" o "2026-10-Q1" (quincena). */
  period: string;
  issueDate: string;
  dueDate: string;
  amount: number;
  /** Desglose por cuenta de depósito (facturas de corte de negocios que lo tienen). */
  items: { label: string; amount: number }[];
  currency: string;
  paidAmount: number;
  balance: number;
  status: InvoiceStatus;
  /** Con saldo y vencimiento pasado (se calcula al leer). */
  overdue: boolean;
  source: InvoiceSource;
  emails: string[];
  payments: InvoicePayment[];
  deliveries: InvoiceDelivery[];
  sentAt: string | null;
  paidAt: string | null;
  paymentNotifiedAt: string | null;
  notes: string | null;
  /** Adeudo: solo se salda a mano y solo lo administra owner o admin reseller. */
  manualSettlement: boolean;
  /** Cotización aceptada de la que nació (`source: "cotizacion"`, #1546). */
  quoteId?: string | null;
  quoteFolio?: string | null;
  lines?: InvoiceLine[];
  subtotal?: number;
  discountTotal?: number;
  taxTotal?: number;
}

export interface InvoiceDraft {
  dealId?: string | null;
  client?: string;
  payer?: string;
  concept?: string;
  amount: number;
  issueDate?: string;
  dueDate?: string;
  emails?: string[];
  notes?: string;
  /** Lo registra como adeudo (requiere rol owner o admin reseller). */
  manualSettlement?: boolean;
}

type RawInvoice = Partial<Invoice> & { id: string };

export function invoiceFromApi(raw: RawInvoice): Invoice {
  const amount = Number(raw.amount) || 0;
  const paidAmount = Number(raw.paidAmount) || 0;
  return {
    id: raw.id,
    number: raw.number || raw.id,
    dealId: raw.dealId ?? null,
    dealTitle: raw.dealTitle ?? null,
    client: raw.client || "—",
    payer: raw.payer ?? null,
    concept: raw.concept || "",
    period: raw.period || String(raw.issueDate || "").slice(0, 7),
    issueDate: raw.issueDate || "",
    dueDate: raw.dueDate || "",
    amount,
    items: Array.isArray(raw.items) ? raw.items : [],
    currency: raw.currency || "MXN",
    paidAmount,
    balance: typeof raw.balance === "number" ? raw.balance : Math.max(0, amount - paidAmount),
    status: (raw.status as InvoiceStatus) || "pendiente",
    overdue: raw.overdue === true,
    source: (raw.source as InvoiceSource) || "manual",
    emails: Array.isArray(raw.emails) ? raw.emails.map(String) : [],
    payments: Array.isArray(raw.payments) ? raw.payments : [],
    deliveries: Array.isArray(raw.deliveries) ? raw.deliveries : [],
    sentAt: raw.sentAt ?? null,
    paidAt: raw.paidAt ?? null,
    paymentNotifiedAt: raw.paymentNotifiedAt ?? null,
    notes: raw.notes ?? null,
    manualSettlement: raw.manualSettlement === true,
    // Factura de una cotización aceptada (#1546).
    quoteId: raw.quoteId ?? null,
    quoteFolio: raw.quoteFolio ?? null,
    lines: Array.isArray(raw.lines) ? raw.lines : [],
    ...(typeof raw.subtotal === "number" ? { subtotal: raw.subtotal } : {}),
    ...(typeof raw.discountTotal === "number" ? { discountTotal: raw.discountTotal } : {}),
    ...(typeof raw.taxTotal === "number" ? { taxTotal: raw.taxTotal } : {}),
  };
}

export async function fetchInvoices(): Promise<Invoice[]> {
  return unwrapList<RawInvoice>(await api.get("/crm/invoicesList")).map(invoiceFromApi);
}

/** Qué le falta al cobro del negocio para que su factura salga sola. */
export type UpcomingIssue = "sin-cobro" | "sin-monto" | "sin-correos";

/**
 * Factura por emitir en el periodo en curso (borrador). No está guardada ni
 * tiene folio: la genera la corrida de corte cuando llega su fecha.
 */
export interface UpcomingInvoice {
  id: string;
  dealId: string | null;
  dealTitle: string | null;
  client: string;
  payer: string | null;
  concept: string;
  period: string;
  /** Fecha de corte en que se emitirá. null = el negocio aún no tiene cobro configurado. */
  issueDate: string | null;
  dueDate: string | null;
  /** null = el negocio no tiene monto de cobro. */
  amount: number | null;
  currency: string;
  emails: string[];
  issues: UpcomingIssue[];
  /** Lo facturado al negocio el mes anterior, como referencia cuando falta el monto. */
  referenceAmount: number | null;
  referencePeriod: string | null;
}

export interface UpcomingInvoices {
  /** Mes en curso ("2026-10"). */
  month: string;
  drafts: UpcomingInvoice[];
}

type RawUpcoming = Partial<UpcomingInvoice> & { id: string };

const ISSUES: UpcomingIssue[] = ["sin-cobro", "sin-monto", "sin-correos"];

export function upcomingFromApi(raw: RawUpcoming): UpcomingInvoice {
  return {
    id: raw.id,
    dealId: raw.dealId ?? null,
    dealTitle: raw.dealTitle ?? null,
    client: raw.client || "—",
    payer: raw.payer ?? null,
    concept: raw.concept || "",
    period: raw.period || "",
    issueDate: raw.issueDate || null,
    dueDate: raw.dueDate || null,
    amount: typeof raw.amount === "number" && raw.amount > 0 ? raw.amount : null,
    currency: raw.currency || "MXN",
    emails: Array.isArray(raw.emails) ? raw.emails.map(String) : [],
    issues: Array.isArray(raw.issues) ? raw.issues.filter((i): i is UpcomingIssue => ISSUES.includes(i as UpcomingIssue)) : [],
    referenceAmount: typeof raw.referenceAmount === "number" && raw.referenceAmount > 0 ? raw.referenceAmount : null,
    referencePeriod: raw.referencePeriod ?? null,
  };
}

/** Borradores del mes en curso: lo que está por emitirse. */
export async function fetchUpcomingInvoices(): Promise<UpcomingInvoices> {
  const data = unwrap<{ month?: string; drafts?: RawUpcoming[] }>(await api.get("/crm/invoicesDrafts"));
  return { month: data?.month || "", drafts: Array.isArray(data?.drafts) ? data.drafts.map(upcomingFromApi) : [] };
}

export const ISSUE_LABEL: Record<UpcomingIssue, string> = {
  "sin-cobro": "Falta la frecuencia y los días de corte",
  "sin-monto": "Falta el monto por periodo",
  "sin-correos": "El cliente no tiene personas con correo en el CRM",
};

export interface PeriodBilling {
  /** Facturas ya emitidas del mes (sin canceladas). */
  issued: number;
  /** Borradores con monto definido. */
  drafted: number;
  /** issued + drafted. */
  total: number;
  /** Borradores a los que les falta el monto: no entran en el total. */
  withoutAmount: number;
}

/** Lo que se factura en el mes: lo emitido más lo que está por emitirse. */
export function periodBilling(invoices: Invoice[], drafts: UpcomingInvoice[], month: string): PeriodBilling {
  const round = (n: number) => Math.round(n * 100) / 100;
  const issued = round(invoices.filter((i) => i.status !== "cancelada" && i.period.startsWith(month)).reduce((s, i) => s + i.amount, 0));
  const inMonth = drafts.filter((d) => d.period.startsWith(month));
  const drafted = round(inMonth.reduce((s, d) => s + (d.amount ?? 0), 0));
  return { issued, drafted, total: round(issued + drafted), withoutAmount: inMonth.filter((d) => d.amount == null).length };
}

export async function createInvoice(draft: InvoiceDraft): Promise<Invoice> {
  return invoiceFromApi(unwrap<RawInvoice>(await api.post("/crm/invoices", draft)));
}

export async function updateInvoice(id: string, patch: Partial<InvoiceDraft>): Promise<Invoice> {
  return invoiceFromApi(unwrap<RawInvoice>(await api.put("/crm/invoices", patch, { params: { id } })));
}

/** Manda la factura a los correos de cobranza del negocio. */
export async function sendInvoice(id: string): Promise<Invoice> {
  return invoiceFromApi(unwrap<RawInvoice>(await api.post("/crm/invoices/send", {}, { params: { id } })));
}

export interface MarkPaidResult {
  invoice: Invoice;
  /** La confirmación de pago salió al cliente. */
  notified: boolean;
  /** Por qué no salió (sin correos de cobranza, fallo del correo…). */
  notifyReason: string | null;
}

/** Registra el pago del saldo y manda la confirmación al cliente. */
export async function markInvoicePaid(id: string, date?: string): Promise<MarkPaidResult> {
  const data = unwrap<RawInvoice & { notified?: boolean; notifyReason?: string | null }>(
    await api.post("/crm/invoices/mark-paid", date ? { date } : {}, { params: { id } }),
  );
  return { invoice: invoiceFromApi(data), notified: data.notified === true, notifyReason: data.notifyReason ?? null };
}

export async function resendPaymentConfirmation(id: string): Promise<Invoice> {
  return invoiceFromApi(unwrap<RawInvoice>(await api.post("/crm/invoices/payment-confirmation", {}, { params: { id } })));
}

export async function cancelInvoice(id: string): Promise<Invoice> {
  return invoiceFromApi(unwrap<RawInvoice>(await api.post("/crm/invoices/cancel", {}, { params: { id } })));
}

function saveBlob(data: BlobPart, filename: string): void {
  const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

/** Un borrador se puede descargar o mandar solo cuando ya tiene fecha de corte y monto. */
export const draftIsComplete = (draft: UpcomingInvoice): boolean => Boolean(draft.issueDate) && draft.amount != null;

/** Descarga el PDF del borrador: la factura tal como saldrá, sin folio. */
export async function downloadDraftPdf(draft: UpcomingInvoice): Promise<void> {
  const res = await api.get("/crm/invoicesDrafts/pdf", { params: { id: draft.id }, responseType: "blob" });
  saveBlob(res.data, `Borrador ${draft.client} ${draft.period}.pdf`);
}

/** Manda el borrador al correo de quien lo pide (nunca al cliente). Devuelve a qué correo salió. */
export async function sendDraftToMe(id: string): Promise<string> {
  const data = unwrap<{ to?: string }>(await api.post("/crm/invoicesDrafts/send", {}, { params: { id } }));
  return data?.to || "";
}

/** Persona del cliente en el CRM y si recibe la factura. */
export interface InvoiceRecipientPerson {
  id: string | null;
  name: string | null;
  email: string | null;
  role: string | null;
  area: string | null;
  receives: boolean;
}

export interface InvoiceRecipients {
  dealId: string;
  client: string | null;
  people: InvoiceRecipientPerson[];
  /** Correos a los que se manda la factura. */
  emails: string[];
  /** Correos guardados antes en el negocio; solo se usan si nadie del cliente tiene correo. */
  legacyEmails: string[];
}

/** A quién de la empresa del cliente se le manda la factura de un negocio. */
export async function fetchInvoiceRecipients(dealId: string): Promise<InvoiceRecipients> {
  const data = unwrap<Partial<InvoiceRecipients>>(await api.get("/crm/invoicesRecipients", { params: { dealId } }));
  return {
    dealId,
    client: data?.client ?? null,
    people: Array.isArray(data?.people) ? data.people : [],
    emails: Array.isArray(data?.emails) ? data.emails.map(String) : [],
    legacyEmails: Array.isArray(data?.legacyEmails) ? data.legacyEmails.map(String) : [],
  };
}

/** Descarga el PDF de la factura. */
export async function downloadInvoicePdf(invoice: Invoice): Promise<void> {
  const res = await api.get("/crm/invoices/pdf", { params: { id: invoice.id }, responseType: "blob" });
  const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = `${invoice.number}.pdf`;
  link.click();
  URL.revokeObjectURL(url);
}

// --- Helpers ----------------------------------------------------------------

const MONTHS_ES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

/** "2026-10" → "octubre 2026"; "2026-10-Q1" → "1.ª quincena de octubre 2026". */
export function periodLabel(period: string): string {
  const match = /^(\d{4})-(\d{2})(?:-Q([12]))?$/.exec(period || "");
  if (!match) return period || "—";
  const month = `${MONTHS_ES[Number(match[2]) - 1] ?? match[2]} ${match[1]}`;
  return match[3] ? `${match[3]}.ª quincena de ${month}` : month;
}

export function invoiceMoney(value: number, currency = "MXN"): string {
  return new Intl.NumberFormat("es-MX", { style: "currency", currency, minimumFractionDigits: 2 }).format(value);
}

/** Estado que ve la persona: una factura con saldo y vencida es «Vencida». */
export function statusLabel(invoice: Invoice): string {
  if (invoice.overdue) return "Vencida";
  return { pendiente: "Pendiente", enviada: "Enviada", parcial: "Parcial", pagada: "Pagada", cancelada: "Cancelada" }[invoice.status] ?? invoice.status;
}

export function apiErrorMessage(err: unknown): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const data = (err as any)?.response?.data;
  return data?.meta?.message || data?.message || "No se pudo completar la acción";
}
